import React, { useEffect, useState } from 'react';
import { useAxios } from '../core/_axios';

interface TimeBlocks {
    morning: number;
    late_morning: number;
    afternoon: number;
    evening: number;
    night: number;
}

interface DayOfWeekData {
    day: number;
    day_name: string;
    count: number;
    percentage: number;
    time_blocks: TimeBlocks;
}

interface AnalyticsData {
    by_day_of_week: DayOfWeekData[];
    total: number;
}

const Analytics: React.FC = () => {
    const axios = useAxios();
    const [signaturesData, setSignaturesData] = useState<AnalyticsData | null>(null);
    const [servicesData, setServicesData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [sigResponse, servResponse] = await Promise.all([
                    axios.get('/api/v1/app/authenticated/current-account/analytics/signatures-by-day-of-week'),
                    axios.get('/api/v1/app/authenticated/current-account/analytics/services-by-day-of-week')
                ]);

                setSignaturesData(sigResponse.data.data);
                setServicesData(servResponse.data.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch analytics:', err);
                setError('Failed to load analytics data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [axios]);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
            </div>
        );
    }

    const renderBarChart = (data: DayOfWeekData[], title: string, subtitle: string) => {
        const maxCount = Math.max(...data.map(d => d.count));

        // Get color intensity based on count (heatmap style)
        const getHeatmapColor = (count: number, max: number) => {
            const intensity = count / max;
            // Red heatmap: light pink to dark red
            if (intensity > 0.7) return '#DC2626'; // red-600
            if (intensity > 0.5) return '#EF4444'; // red-500
            if (intensity > 0.3) return '#F87171'; // red-400
            if (intensity > 0.1) return '#FCA5A5'; // red-300
            return '#FEE2E2'; // red-100
        };

        // Colorblind-friendly colors for time blocks
        const timeBlockConfig = [
            { key: 'morning' as const, label: '7am-11am', color: '#0891B2', name: 'Morning' },
            { key: 'late_morning' as const, label: '11am-3pm', color: '#F59E0B', name: 'Late Morning' },
            { key: 'afternoon' as const, label: '3pm-7pm', color: '#8B5CF6', name: 'Afternoon' },
            { key: 'evening' as const, label: '7pm-10pm', color: '#EC4899', name: 'Evening' },
            { key: 'night' as const, label: '10pm-7am', color: '#1F2937', name: 'Night' }
        ];

        const renderTimeBlocks = (timeBlocks: TimeBlocks, total: number) => {
            if (total === 0) return null;

            return (
                <div className="mt-1 flex gap-0.5 h-4 rounded">
                    {timeBlockConfig.map(config => {
                        const count = timeBlocks[config.key];
                        const percentage = (count / total) * 100;
                        if (count === 0) return null;

                        return (
                            <div
                                key={config.key}
                                className="relative group cursor-pointer hover:brightness-110"
                                style={{
                                    width: `${percentage}%`,
                                    backgroundColor: config.color
                                }}
                            >
                                {/* Custom tooltip */}
                                <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 bg-gray-900 text-white text-xs rounded px-1.5 py-0.5 shadow-lg whitespace-nowrap">
                                    <div className="font-semibold text-xs">{config.name} {config.label}</div>
                                    <div className="text-xs">{count.toLocaleString()} ({Math.round(percentage)}%)</div>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2">
                                        <div className="border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-lg font-semibold mb-1">{title}</h3>
                <p className="text-gray-600 text-xs mb-4">{subtitle}</p>

                <div className="space-y-3">
                    {data.map((item) => {
                        const heatmapColor = getHeatmapColor(item.count, maxCount);

                        return (
                            <div key={item.day}>
                                <div className="flex items-center gap-3">
                                    <div className="w-20 text-xs font-bold text-gray-700">
                                        {item.day_name}
                                    </div>
                                    <div className="flex-1">
                                        <div className="relative h-8 bg-gray-100 rounded">
                                            <div
                                                className="absolute h-full rounded transition-all duration-500"
                                                style={{
                                                    width: `${(item.count / maxCount) * 100}%`,
                                                    backgroundColor: heatmapColor
                                                }}
                                            ></div>
                                            <div className="absolute inset-0 flex items-center px-2 text-xs font-bold text-gray-900">
                                                {item.count.toLocaleString()} ({item.percentage}%)
                                            </div>
                                        </div>
                                        {renderTimeBlocks(item.time_blocks, item.count)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="mt-4 pt-3 border-t">
                    <div className="text-xs text-gray-500 mb-2">
                        Hover over colored segments to see time-of-day breakdown
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                        {timeBlockConfig.map(config => (
                            <div key={config.key} className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: config.color }}></div>
                                <span>{config.name} ({config.label})</span>
                            </div>
                        ))}
                    </div>
                    <div className="text-xs text-gray-600">
                        <strong>Total:</strong> {data.reduce((sum, d) => sum + d.count, 0).toLocaleString()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {signaturesData && renderBarChart(
                    signaturesData.by_day_of_week,
                    'Signatures by Day of Week',
                    'When do people typically sign guestbooks?'
                )}

                {servicesData && renderBarChart(
                    servicesData.by_day_of_week,
                    'Services by Day of Week',
                    'When are services typically created?'
                )}
            </div>
        </div>
    );
};

export default Analytics;
